/**
 * Container Component - Replaces HTML div elements
 */

import React from "react";

type ContainerElement =
	| "div"
	| "section"
	| "main"
	| "header"
	| "footer"
	| "article";

type ContainerProps<E extends ContainerElement = "div"> = {
	as?: E;
	children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<E>, "as" | "children">;

// Internal implementation with forwardRef
// Using HTMLElement as the ref type is safe since all ContainerElement types extend it
const ContainerInternal = React.forwardRef<
	HTMLElement,
	ContainerProps<ContainerElement>
>(({ as, children, ...rest }, ref) => {
	const Component = (as ?? "div") as ContainerElement;

	// Type assertion is necessary for polymorphic components with forwardRef.
	// TypeScript cannot narrow ref types based on the 'as' prop at compile time.
	// This is safe because all ContainerElement types (div, section, main, etc.)
	// extend HTMLElement, and the exported type signature ensures type safety at usage.
	return (
		<Component
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			ref={ref as any}
			{...(rest as React.ComponentPropsWithoutRef<ContainerElement>)}
		>
			{children}
		</Component>
	);
});

ContainerInternal.displayName = "Container";

// Export with proper generic typing
const Container = ContainerInternal as <E extends ContainerElement = "div">(
	props: ContainerProps<E> & { ref?: React.Ref<HTMLElement> }
) => React.ReactElement;

export default Container;
